import { useQueryClient } from '@tanstack/react-query';

import type { TypeCheckInOutStatus, TypeCodeState, TypeWorkInfo } from '../../types';
import { axiosInstance } from '~/services/fetchServiceV2';
import { TypeErrorFormat, requestHelper } from '~/utils/fetchHelper';

/** @category useGetCommuteInfo */
export type TypeGetCommuteInfoResponse = {
  code: string;
  message: string;
  data: {
    /**
     * 출근 상태
     * @remarks
     * - START_WORK(출근 완료)
     * - END_WORK(업무 중이 아님)
     * - EARLY_END_WORK(최근에 조퇴 진행함)
     */
    status: TypeCodeState;
    data: Omit<TypeWorkInfo, 'status' | 'recentCheckInOut' | 'workType'> | null;
    /** 최근 체크인/체크아웃 정보 */
    recentCheckInOut: TypeCheckInOutStatus | null;
    /** 근무 유형 */
    workType: TypeCodeState | null;
  };
};

/**
 * @description 작업자의 현재 출근 및 체크인 정보를 가져오는 api 요청에 사용되는 커스텀 훅
 */
export const useGetCommuteInfo = () => {
  const queryClient = useQueryClient();

  const requestGetCommuteInfo = async ({
    onSuccess,
    onError,
    showLoading = true,
  }: {
    onSuccess: (response: TypeGetCommuteInfoResponse) => void;
    onError: (error: TypeErrorFormat) => void;
    showLoading?: boolean;
  }) => {
    const endpoint = '/commutes/current';
    const query = queryClient.ensureQueryData({
      queryKey: ['commutes', 'current'],
      queryFn: () => axiosInstance.get(endpoint),
      gcTime: 0,
    });

    return requestHelper(() => query, onSuccess, onError, undefined, { showLoading });
  };

  return requestGetCommuteInfo;
};
